import type { ColDef, ColGroupDef, GridOptions } from '@ag-grid-community/core';
import { BatteryHealth, DeviceStateAnalysisAsset } from '@cdw-ae/common-data';
import type {
  FilterBarField,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import { map, Observable } from 'rxjs';
import { TailwindColors } from '../../../../../common/constants/tailwind-colors';
import { DevicesDataService } from '../../../../../common/services/device/devices-data.service';
import { OuPathService } from '../../../../../common/services/ou-path/ou-path.service';
import {
  filterToSupportedStateAnalysisFields,
  StateAnalysisReportFilterSettings,
} from '../../../../../common/services/route-config/configs/filter-bar-config.config';
import { createBatteryHealthReportConfig } from '../configs/battery-health-report.config';
import type {
  GaugeSummaryConfig,
  ReportData,
} from '../report-viewer/report-viewer.model';
import { BaseReportHandler } from './base-report.handler';

/**
 * Handler for Battery Health by OU aggregate reports
 * Manages filtering, grid configuration, and data processing for Battery Health reports
 * Structure: Organizational Unit Path (hierarchical tree)
 */
export class BatteryHealthReportHandler extends BaseReportHandler {
  /**
   * Static method to load Battery Health report data from API
   * Fetches device state analysis data, processes it with filters, and returns configured report data
   * @param devicesDataService Service to fetch device data
   * @param filters Optional filters to apply to the data
   * @returns Observable of ReportData ready for display
   */
  static load(
    devicesDataService: DevicesDataService,
    filters?: SelectedFilterField[]
  ): Observable<ReportData> {
    // Get filters to apply (use provided filters or initial filters)
    const allFilters =
      filters || BatteryHealthReportHandler.getInitialFilters() || [];

    // Filter to only include fields supported by the state-analysis endpoint
    const filtersToApply = filterToSupportedStateAnalysisFields(allFilters);

    // Pass filters to API - filtering happens server-side now
    return devicesDataService.getDeviceStateAnalysis(filtersToApply).pipe(
      map((deviceStateAnalysisResponse) => {
        // Aggregate data (no filtering needed - API already filtered)
        const processedData = BatteryHealthReportHandler.processRawData(
          deviceStateAnalysisResponse.data,
          [] // Empty filters - already filtered by API
        );

        // Create and return report config with processed data
        return createBatteryHealthReportConfig(processedData);
      })
    );
  }

  private totalDevicesCount = 0;
  private batterySummary = {
    normal: 0,
    replaceSoon: 0,
    replaceNow: 0,
    unknown: 0,
  };
  private batterySummaryPercentages = {
    normal: 0,
    replaceSoon: 0,
    replaceNow: 0,
    unknown: 0,
  };

  // Gauge colors configuration — Figma donut palette (saturated fills, not pastel row backgrounds)
  private readonly gaugeColors = {
    green: [{ name: 'Green', value: TailwindColors.chartGreen }],
    orange: [{ name: 'Orange', value: TailwindColors.chartDarkOrange }],
    red: [{ name: 'Red', value: TailwindColors.chartRed }],
    gray: [{ name: 'Gray', value: TailwindColors.gray200 }],
  };

  /**
   * Calculate battery health summary statistics from the data
   * Filters to leaf nodes and groups by battery health status
   */
  private calculateBatterySummary(data: unknown[]): void {
    if (!data || data.length === 0) {
      return;
    }

    // Cast data to expected structure
    const batteryData = data as Array<{
      ouPath: string;
      normal?: number;
      replaceSoon?: number;
      replaceNow?: number;
      unknown?: number;
    }>;

    // Reset summary
    this.batterySummary = {
      normal: 0,
      replaceSoon: 0,
      replaceNow: 0,
      unknown: 0,
    };
    this.totalDevicesCount = 0;

    // Sum all rows — each row represents devices directly in that OU (no parent-child overlap)
    batteryData.forEach((item) => {
      this.batterySummary.normal += item.normal || 0;
      this.batterySummary.replaceSoon += item.replaceSoon || 0;
      this.batterySummary.replaceNow += item.replaceNow || 0;
      this.batterySummary.unknown += item.unknown || 0;
    });

    // Calculate total devices
    this.totalDevicesCount =
      this.batterySummary.normal +
      this.batterySummary.replaceSoon +
      this.batterySummary.replaceNow +
      this.batterySummary.unknown;

    // Calculate percentages for gauges (0-100)
    if (this.totalDevicesCount > 0) {
      this.batterySummaryPercentages = {
        normal: Math.round(
          (this.batterySummary.normal / this.totalDevicesCount) * 100
        ),
        replaceSoon: Math.round(
          (this.batterySummary.replaceSoon / this.totalDevicesCount) * 100
        ),
        replaceNow: Math.round(
          (this.batterySummary.replaceNow / this.totalDevicesCount) * 100
        ),
        unknown: Math.round(
          (this.batterySummary.unknown / this.totalDevicesCount) * 100
        ),
      };
    } else {
      this.batterySummaryPercentages = {
        normal: 0,
        replaceSoon: 0,
        replaceNow: 0,
        unknown: 0,
      };
    }
  }

  /**
   * Initialize the handler with data to calculate battery health summary
   */
  public override initializeWithData(data: unknown[]): void {
    this.calculateBatterySummary(data);
  }

  /**
   * Get gauge summary configuration for the Battery Health report
   * @param filteredData Optional filtered data to calculate summary from
   */
  public override getGaugeSummaryConfig(
    filteredData?: unknown[]
  ): GaugeSummaryConfig {
    // If filtered data is provided, recalculate the summary
    if (filteredData && filteredData.length > 0) {
      this.calculateBatterySummary(filteredData);
    }

    return {
      headerText: `${this.totalDevicesCount} Devices`,
      cards: [
        {
          title: 'Normal',
          value: this.batterySummary.normal,
          percentage: this.batterySummaryPercentages.normal,
          borderColor: TailwindColors.chartGreen,
          gaugeColor: this.gaugeColors.green,
          iconImage: 'images/reports/battery_health_by_ou/battery_full.svg',
          showValue: false,
        },
        {
          title: 'Replace Soon',
          value: this.batterySummary.replaceSoon,
          percentage: this.batterySummaryPercentages.replaceSoon,
          borderColor: TailwindColors.chartDarkOrange,
          gaugeColor: this.gaugeColors.orange,
          iconImage: 'images/reports/battery_health_by_ou/battery_low.svg',
          showValue: false,
        },
        {
          title: 'Replace Now',
          value: this.batterySummary.replaceNow,
          percentage: this.batterySummaryPercentages.replaceNow,
          borderColor: TailwindColors.chartRed,
          gaugeColor: this.gaugeColors.red,
          iconImage: 'images/reports/battery_health_by_ou/battery_alert.svg',
          showValue: false,
        },
        {
          title: 'Unknown',
          value: this.batterySummary.unknown,
          percentage: this.batterySummaryPercentages.unknown,
          borderColor: TailwindColors.gray200,
          gaugeColor: this.gaugeColors.gray,
          iconImage: 'images/reports/battery_health_by_ou/battery_unknown.svg',
          showValue: false,
        },
      ],
    };
  }

  getColumnDefs(): (ColDef | ColGroupDef)[] {
    const config = this.reportData.aggregateConfig;
    if (!config) return [];

    // Add aggregation to the columns while preserving all existing properties
    return config.columns.map((col) => {
      if ('field' in col && col.field) {
        // Add aggregation for numeric columns (Normal, Replace Soon, Replace Now, Unknown)
        const numericFields = [
          'normal',
          'replaceSoon',
          'replaceNow',
          'unknown',
        ];
        if (numericFields.includes(col.field)) {
          return {
            ...col,
            aggFunc: 'sum', // Aggregate child values by summing
          };
        }
      }
      return col;
    });
  }

  getGridOptions(): GridOptions {
    const config = this.reportData.aggregateConfig;
    if (!config) return {};

    return {
      treeData: true,
      animateRows: true,
      groupDefaultExpanded: 0, // Start collapsed
      suppressColumnVirtualisation: true, // Force all columns to render
      defaultColDef: {
        resizable: true,
      },
      autoGroupColumnDef: {
        headerName: 'OU Path',
        minWidth: 400,
        cellRendererParams: {
          suppressCount: true,
        },
      },
      getDataPath: (data) => {
        // Convert ouPath segments - first segment no slash, subsequent ones with slash
        // Example: "/Engineering/Development" -> ["Engineering", "/Development"]
        // Note: Status is stored in each row but not part of the tree hierarchy
        // When multiple rows share the same path (e.g., same OU but different status),
        // AG Grid will aggregate them using aggFunc='sum'
        const path = (data as { ouPath?: string }).ouPath || '';
        return OuPathService.parsePathForGrid(path);
      },
    };
  }

  static override getFilterBarSettings(): FilterBarField[] {
    return StateAnalysisReportFilterSettings;
  }

  static override getInitialFilters(): SelectedFilterField[] | null {
    return BaseReportHandler.getDefaultActiveStatusFilter(
      StateAnalysisReportFilterSettings
    );
  }

  /**
   * Process raw DeviceStateAnalysisAsset data into battery health by OU format
   * Aggregates data by organizational unit
   */
  static override processRawData(
    rawData: unknown[],
    _filters: SelectedFilterField[]
  ): unknown[] {
    const deviceData = rawData as DeviceStateAnalysisAsset[];

    // Aggregate battery health data by OU
    const batteryHealthSummaryLookupByOu = deviceData.reduce(
      (accumulator, currentValue) => {
        const ouPath = currentValue.groupingValue || 'Unknown OU';

        if (!accumulator[ouPath]) {
          accumulator[ouPath] = {
            ouPath,
            status: currentValue.status,
            normal: 0,
            replaceSoon: 0,
            replaceNow: 0,
            unknown: 0,
          } as Record<string, string | number>;
        }

        if (String(currentValue.batteryHealth) === BatteryHealth.SOON) {
          accumulator[ouPath].replaceSoon =
            Number(accumulator[ouPath].replaceSoon) + currentValue.deviceCount;
        } else if (String(currentValue.batteryHealth) === BatteryHealth.NOW) {
          accumulator[ouPath].replaceNow =
            Number(accumulator[ouPath].replaceNow) + currentValue.deviceCount;
        } else if (
          String(currentValue.batteryHealth) === BatteryHealth.NORMAL
        ) {
          accumulator[ouPath].normal =
            Number(accumulator[ouPath].normal) + currentValue.deviceCount;
        } else {
          accumulator[ouPath].unknown =
            Number(accumulator[ouPath].unknown) + currentValue.deviceCount;
        }

        return accumulator;
      },
      {} as Record<string, Record<string, string | number>>
    );

    const rows = Object.values(batteryHealthSummaryLookupByOu) as Array<
      { ouPath: string } & Record<string, unknown>
    >;
    return OuPathService.redistributeNonLeafData(rows, [
      'normal',
      'replaceSoon',
      'replaceNow',
      'unknown',
    ]);
  }
}
